import { Box, Button, Collapse, Flex, FormLabel, Icon, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { IconQuestionMark } from 'assets/icons';
import { FormikInput, FormikSelect, FormikSwitch } from 'components/Formik';
import LogoLoader from 'components/Loader/LogoLoader';
import Title from 'components/Title';
import FEATURES from 'global/features';
import { PLANS } from 'global/plans';
import { useFetchSettings } from 'hooks/companySettings/useFetchSettings';
import {
  IUpdateSegmentedTrackingSettings,
  useUpdateSegmentedTrackingSettings,
} from 'hooks/companySettings/useUpdateSegmentedTrackingSettings';
import { useFeatureList } from 'store/useFeatureStore';
import { useCompanyInfo } from 'store/useUserStore';

import { initialSegmentedTrackingData, segmentedTrackingSettingsValidation } from './schema';
import { segmentReportOptions } from '../shared/options';

const SegmentedTracking: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'companySettings']);

  const [formData, setFormData] = useState<any>(initialSegmentedTrackingData);

  const companyInfo: any = useCompanyInfo();
  const featureList: any = useFeatureList();

  const isPremium =
    companyInfo?.plan?.toLowerCase() === PLANS.freeTrial ||
    companyInfo?.plan?.toLowerCase() === PLANS.premium ||
    featureList[FEATURES.segmented_tracking];

  const { data, status, refetch } = useFetchSettings('segmented_tracking');
  const { status: updateSegmentedTrackingSettingsStatus, mutateAsync: updateSegmentedTrackingSettings } =
    useUpdateSegmentedTrackingSettings();

  useEffect(() => {
    if (status === 'success') {
      const pFormData: typeof initialSegmentedTrackingData = {
        drivingSpeedThreshold: data.payload?.drivingSpeedThreshold,
        idleIntervalThresholdInMinutes: data.payload?.idleIntervalThresholdInMinutes,
        attribute_mileage_duration_to_job: data?.payload?.attribute_mileage_duration_to_job,
        attribute_to: data?.payload.attribute_to
          ? segmentReportOptions.find((item: any) => item.value === data?.payload?.attribute_to)
          : '',
      };
      setFormData(pFormData);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleFormSubmit = async (values: typeof initialSegmentedTrackingData) => {
    // Extract only the value from attribute_to
    const { attribute_to, ...restValues } = values;
    const postData: IUpdateSegmentedTrackingSettings = {
      ...restValues,
      attribute_to: attribute_to.value,
    };

    updateSegmentedTrackingSettings(postData).then(() => refetch());
  };

  const handleToggle = (
    e: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    controlName: string
  ) => {
    setFieldValue(controlName, e.target.checked);
  };

  if (status === 'loading') return <LogoLoader />;

  return (
    <Box>
      <Box mb={{ base: '2', md: '0' }}>
        <Title hideBackButton> {t('companySettings:segmentedTracking')}</Title>
      </Box>
      <Box mt="26px">
        <Formik
          initialValues={formData}
          validationSchema={segmentedTrackingSettingsValidation}
          enableReinitialize
          onSubmit={handleFormSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Box>
                <FormikInput
                  label={
                    <>
                      {t('companySettings:drivingSpeedThreshold') + ` (${data?.payload?.drivingSpeedUnit})`}{' '}
                      <Flex gridGap={2}>
                        {!isPremium ? (
                          <Text fontSize="xs" color="secondary.700">
                            ({t('common:premium')})
                          </Text>
                        ) : null}
                        <Tooltip label={t('companySettings:speedConsideredToBeDriving')} hasArrow bg="black">
                          <IconButton
                            aria-label={t('companySettings:speedConsideredToBeDriving')}
                            variant="unstyled"
                            icon={<Icon as={IconQuestionMark} />}
                            size="xs"
                          />
                        </Tooltip>
                      </Flex>
                    </>
                  }
                  name="drivingSpeedThreshold"
                  type="number"
                  inputProps={{
                    size: 'md',
                    w: '20%',
                  }}
                  formControlProps={{
                    isDisabled: !isPremium,
                  }}
                  labelProps={{
                    display: 'flex',
                    alignItems: 'center',
                    gridGap: '1',
                  }}
                />
                <FormikInput
                  label={
                    <>
                      {t('companySettings:idleIntervalThresholdInMinutes')}
                      <Flex gridGap={2}>
                        {!isPremium ? (
                          <Text fontSize="xs" color="secondary.700">
                            ({t('common:premium')})
                          </Text>
                        ) : null}
                        <Tooltip label={t('companySettings:durationSegmentsConsiderIdle')} hasArrow bg="black">
                          <IconButton
                            aria-label={t('companySettings:durationSegmentsConsiderIdle')}
                            variant="unstyled"
                            icon={<Icon as={IconQuestionMark} />}
                            size="xs"
                          />
                        </Tooltip>
                      </Flex>
                    </>
                  }
                  name="idleIntervalThresholdInMinutes"
                  type="number"
                  inputProps={{
                    size: 'md',
                    w: '20%',
                  }}
                  formControlProps={{
                    isDisabled: !isPremium,
                  }}
                  labelProps={{
                    display: 'flex',
                    alignItems: 'center',
                    gridGap: '1',
                  }}
                />

                <Box display="flex" mb="17px">
                  <FormLabel fontSize="sm">{t('companySettings:assignMileageDurationToJob')}</FormLabel>
                  <FormikSwitch
                    name="attribute_mileage_duration_to_job"
                    onChangeCallback={e => handleToggle(e, setFieldValue, 'attribute_mileage_duration_to_job')}
                  ></FormikSwitch>
                </Box>

                <Collapse in={values.attribute_mileage_duration_to_job} unmountOnExit={true} animateOpacity>
                  <Box maxWidth="390px">
                    <FormikSelect
                      name="attribute_to"
                      label={t('companySettings:assignTo')}
                      options={segmentReportOptions}
                      selectProps={{
                        placeholder: t('companySettings:selectOption'),
                        isClearable: false,
                      }}
                    />
                  </Box>
                </Collapse>

                <Button
                  type="submit"
                  isLoading={updateSegmentedTrackingSettingsStatus === 'loading'}
                  isDisabled={!isPremium}
                >
                  {t('common:save')}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default SegmentedTracking;
